import { useMemo } from 'react';
import { Configuration, ProductListsApi } from '@/api';
import { productListQueryKeys } from '@/api/queries/productListQueryKeys';
import { EmptyResult } from '@/models/Pagination';
import { ProductListSkuIds } from '@/models/ProductList';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const initialState = EmptyResult<string>();
const itemsPerPage = 10;

const useGetProductListSkuIdsQuery = (productListId?: string, page = 1, enabled = true) => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const productListsApi = useMemo(() => new ProductListsApi(new Configuration(auth)), [auth]);
  const queryKey = productListQueryKeys.skuIds(productListId ?? '', page);

  const query = useQuery({
    queryKey,
    queryFn: () => {
      if (!productListId) throw new Error('Missing productListId');
      return productListsApi.getProductListSkuIds(
        productListId,
        page * itemsPerPage - itemsPerPage,
        itemsPerPage,
      );
    },
    enabled: enabled && Boolean(productListId),
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey });

  return {
    skuIds: (query.data ?? initialState) as ProductListSkuIds,
    itemsPerPage,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    invalidateQuery,
  };
};

export default useGetProductListSkuIdsQuery;
